import { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { BellIcon } from './Icons';
import { useNotificationStore } from '../stores/useNotificationStore';
import { api } from '../lib/api';
import { T } from '../lib/design-tokens';
import type { NotificationRecord } from '../types';

function timeAgo(ts: string) {
  const diff = Date.now() - new Date(ts).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

export function NotificationBell() {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const { notifications, unreadCount, markRead, markAllRead, setNotifications } =
    useNotificationStore();

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    if (open) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  const handleOpen = useCallback(() => {
    const next = !open;
    setOpen(next);
    if (next) {
      api<NotificationRecord[]>('/notifications')
        .then(setNotifications)
        .catch(() => {});
    }
  }, [open, setNotifications]);

  const handleClickNotification = async (n: NotificationRecord) => {
    if (!n.read) {
      markRead(n.id);
      api(`/notifications/${n.id}/read`, { method: 'PATCH' }).catch(() => {});
    }
    if (n.taskId) {
      setOpen(false);
      navigate(`/operations/${n.taskId}`);
    }
  };

  const handleMarkAllRead = async () => {
    markAllRead();
    api('/notifications/read-all', { method: 'POST' }).catch(() => {});
  };

  return (
    <div ref={ref} className="relative">
      <button
        onClick={handleOpen}
        className="relative p-2 rounded-lg transition-colors"
        style={{ color: T.cream35 }}
        onMouseEnter={(e) => (e.currentTarget.style.color = T.cream60)}
        onMouseLeave={(e) => (e.currentTarget.style.color = T.cream35)}
      >
        <BellIcon size={20} />
        {unreadCount > 0 && (
          <span
            className="absolute -top-0.5 -right-0.5 flex items-center justify-center rounded-full font-bold"
            style={{
              width: 18,
              height: 18,
              fontSize: 10,
              background: T.gold,
              color: T.bg,
              animation: 'pop 0.3s ease',
            }}
          >
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div
          className="absolute right-0 top-full mt-2 overflow-hidden"
          style={{
            width: 360,
            maxHeight: 400,
            background: T.surface,
            border: `1px solid ${T.glassBorder}`,
            borderRadius: 14,
            boxShadow: '0 12px 40px rgba(0,0,0,0.4)',
            zIndex: 100,
          }}
        >
          <div
            className="flex items-center justify-between px-5 py-3.5"
            style={{ borderBottom: `1px solid ${T.glassBorder}` }}
          >
            <span className="font-semibold text-sm" style={{ color: T.cream, fontFamily: T.sans }}>
              Notifications
            </span>
            {unreadCount > 0 && (
              <button
                onClick={handleMarkAllRead}
                className="text-xs font-medium border-none bg-transparent cursor-pointer"
                style={{ color: T.gold, fontFamily: T.mono }}
              >
                Mark all as read
              </button>
            )}
          </div>

          <div className="overflow-auto" style={{ maxHeight: 340 }}>
            {notifications.length === 0 ? (
              <div className="py-10 text-center text-sm" style={{ color: T.cream35, fontFamily: T.sans }}>
                No notifications yet.
              </div>
            ) : (
              notifications.map((n) => (
                <div
                  key={n.id}
                  onClick={() => handleClickNotification(n)}
                  className="flex items-start gap-3 px-5 py-3 cursor-pointer transition-colors"
                  style={{
                    background: n.read ? 'transparent' : T.goldGlow,
                    borderBottom: `1px solid ${T.cream04}`,
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.background = T.cream04)}
                  onMouseLeave={(e) =>
                    (e.currentTarget.style.background = n.read ? 'transparent' : T.goldGlow)
                  }
                >
                  <div
                    className="w-1.5 h-1.5 rounded-full mt-2 shrink-0"
                    style={{
                      background:
                        n.type === 'task_update'
                          ? T.gold
                          : n.type === 'usage_warning'
                            ? T.amber
                            : T.cream35,
                    }}
                  />
                  <div className="min-w-0 flex-1">
                    <div className="text-[13px] font-semibold" style={{ color: T.cream, fontFamily: T.sans }}>
                      {n.title}
                    </div>
                    <div className="text-xs mt-0.5 truncate" style={{ color: T.cream60, fontFamily: T.sans }}>
                      {n.message}
                    </div>
                    <div className="text-[11px] mt-1" style={{ color: T.cream12, fontFamily: T.mono }}>
                      {timeAgo(n.createdAt)}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
